import store from "@packages/store";
import moment from "moment";
import Vue from "vue";
import { i18n } from "@packages/I18n"

export default {
    async overview() {
        const overview = await Vue.api.core.statistics.overview();

        const engamentPercentage = (
            (overview.engagement / overview.impress) *
            100
        ).toFixed(2);

        this.setOverviewAccessData(overview.accesses);

        store.commit("STATISTICS_SET_OVERVIEW", {
            ...overview,
            engamentPercentage: isNaN(engamentPercentage)
                ? 0
                : engamentPercentage
        });

        return overview;
    },

    setOverviewAccessData(accessInfo) {
        const completeAccessChart = {
            label: i18n.t('message.statistics.message.completed'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [completeAccessChart],
            count: 0
        };

        const orderedDates = orderDates(Object.keys(accessInfo));

        orderedDates.forEach(key => {
            const value = accessInfo[key];

            completeAccessChart.data.push(value);

            mappedChart.labels.push(
                formatDate({
                    date: key,
                    datasetSize: value
                })
            );

            mappedChart.count += value;
        });

        store.commit("STATISTICS_SET_ACCESS", mappedChart);
    },

    setData({ statistics, heatTableData, topLocalAccesses }) {
        this.setInteractionData({
            interactionData: statistics.interactionData
        });

        this.setAgeAverageData({
            ageData: statistics.age
        });

        this.setScreenTimeAvgData({
            screenTime: statistics.screenTime,
            interactions: statistics.rejection + statistics.engagement
        });

        this.setAgeData({
            ageData: statistics.age
        });

        this.setRejectionData({
            rejectionData: statistics.rejection,
            engagementData: statistics.engagement
        });

        this.setEngagementData({
            engagementData: statistics.engagement,
            rejectionData: statistics.rejection
        });

        this.setDeviceTypeData({
            deviceTypeData: statistics.deviceType
        });

        this.setRegisterTypeData({
            registerTypeData: statistics.registerType
        });

        this.setGenderData({
            genderData: statistics.gender
        });

        this.accessHourData({
            accessHourData: statistics.accessHour
        });

        this.setAccessWeekDayData({
            weekdayData: statistics.accessDayWeek
        });

        this.setAccessData(statistics.dayInfo);

        this.setUserData(statistics.dayInfo);

        this.setImpressionsData(statistics.dayInfo);

        this.setheatTableData(heatTableData);

        this.setTopLocalsData(topLocalAccesses)
    },

    setheatTableData(heatTableData) {
        store.commit(
            "STATISTICS_SET_HEAT_MAP",
            heatTableData
        );
    },

    setAdminData(statistics) {
        this.setConnectedUsersData({
            connectedUsers: statistics.connectedUsers
        });

        this.setInteractionData({
            interactionData: statistics.interactionData
        });

        this.setScreenTimeAvgData({
            screenTime: statistics.screenTime,
            interactions: statistics.rejection + statistics.engagement
        });

        this.setRejectionData({
            rejectionData: statistics.rejection,
            engagementData: statistics.engagement
        });

        this.setEngagementData({
            engagementData: statistics.engagement,
            rejectionData: statistics.rejection
        });

        this.setDeviceTypeData({
            deviceTypeData: statistics.deviceType
        });

        this.accessHourData({
            accessHourData: statistics.accessHour
        });

        this.setAccessWeekDayData({
            weekdayData: statistics.accessDayWeek
        });

        this.setAdminAccessData(statistics.dayInfo);

        this.setUserData(statistics.dayInfo);

        this.setImpressionsData(statistics.dayInfo);

        this.setTotalUserData(statistics.dayInfo);
    },

    async load(filters) {
        const [statistics, heatTableData, topLocalAccesses] = await Promise.all([
            Vue.api.core.statistics.load(filters),
            Vue.api.core.statistics.heatTable(filters),
            Vue.api.core.statistics.getTopLocalAccess(filters)
        ]);

        this.setData({ statistics, heatTableData, topLocalAccesses });
    },

    setConnectedUsersData({ connectedUsers }) {
        store.commit("STATISTICS_SET_CONNECTED_USERS", connectedUsers);
    },

    setInteractionData({ interactionData }) {
        let mappedInteractionData;

        if (interactionData) {
            mappedInteractionData = Object.keys(interactionData).map(key => {
                return {
                    ...interactionData[key],
                    id: key
                };
            });
        }

        store.commit("STATISTICS_SET_INTERACTION_DATA", mappedInteractionData);
    },

    setScreenTimeAvgData({ screenTime, interactions }) {
        const averageTime = (screenTime / interactions).toFixed(2);

        store.commit(
            "STATISTICS_SET_SCREEN_TIME_AVG",
            isNaN(averageTime) || !isFinite(averageTime) ? 0 : averageTime
        );
    },

    setAgeAverageData({ ageData }) {
        const ageDataKeys = Object.keys(ageData);

        const denominator = ageDataKeys.reduce((acc, key) => {
            const value = ageData[key];

            return acc + value * key;
        }, 0);

        const total = ageDataKeys.reduce((acc, key) => {
            const value = ageData[key];

            return acc + value;
        }, 0);

        const ageAverage = denominator / total;

        store.commit(
            "STATISTICS_SET_AGE_AVG",
            isNaN(ageAverage) ? 0 : ageAverage.toFixed(0)
        );
    },

    setRejectionData({ rejectionData, engagementData }) {
        const rejection = (
            (rejectionData / (rejectionData + engagementData)) *
            100
        ).toFixed(0);

        store.commit(
            "STATISTICS_SET_REJECTION",
            isNaN(rejection) ? 0 : rejection
        );
    },

    setEngagementData({ engagementData, rejectionData }) {
        const engagement = (
            (engagementData / (rejectionData + engagementData)) *
            100
        ).toFixed(0);

        store.commit(
            "STATISTICS_SET_ENGAGEMENT",
            isNaN(engagement) ? 0 : engagement
        );
    },

    setAgeData({ ageData }) {
        const ageChart = {
            label: i18n.t('message.statistics.message.age'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [ageChart],
            count: 0
        };

        Object.keys(ageData).forEach(key => {
            const value = ageData[key];

            ageChart.data.push(value);

            mappedChart.labels.push(key);

            mappedChart.count += value;
        });

        store.commit("STATISTICS_SET_AGE", mappedChart);
    },

    setDeviceTypeData({ deviceTypeData }) {
        const deviceTypeChart = {
            label: i18n.t('message.statistics.message.devices'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [deviceTypeChart],
            count: 0
        };

        Object.keys(deviceTypeData).forEach((key, index) => {
            if (index > 4) {
                return;
            }

            const value = deviceTypeData[key];

            deviceTypeChart.data.push(value);

            mappedChart.labels.push(parseDeviceType(key));

            mappedChart.count += value;
        });

        store.commit("STATISTICS_SET_DEVICE_TYPE", mappedChart);
    },

    setRegisterTypeData({ registerTypeData }) {
        const registerTypeChart = {
            label: i18n.t('message.statistics.message.registrationType'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [registerTypeChart],
            count: 0
        };

        Object.keys(registerTypeData).forEach(key => {
            const value = registerTypeData[key];

            registerTypeChart.data.push(value);

            mappedChart.labels.push(parseRegisterType(key));

            mappedChart.count += value;
        });

        store.commit("STATISTICS_SET_REGISTER_TYPE", mappedChart);
    },

    setGenderData({ genderData }) {
        const genderChart = {
            label: i18n.t('message.statistics.message.gender'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [genderChart],
            count: 0
        };

        Object.keys(genderData).forEach(key => {
            const value = genderData[key];

            genderChart.data.push(value);

            mappedChart.labels.push(parseGender(key));

            mappedChart.count += value;
        });

        store.commit("STATISTICS_SET_GENDER", mappedChart);
    },

    accessHourData({ accessHourData }) {
        const accessHourChart = {
            label: i18n.t('message.statistics.message.accessesByHour'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [accessHourChart],
            count: 0
        };

        Object.keys(accessHourData).forEach(key => {
            const value = accessHourData[key];

            accessHourChart.data.push(value);

            mappedChart.labels.push(`${key}h`);

            mappedChart.count += value;
        });

        store.commit("STATISTICS_SET_ACCESS_HOUR", mappedChart);
    },

    setAccessWeekDayData({ weekdayData }) {
        const weekDayChart = {
            label: i18n.t('message.statistics.message.accessesByDay'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [weekDayChart],
            count: 0
        };

        Object.keys(weekdayData).forEach(key => {
            const value = weekdayData[key];

            weekDayChart.data.push(value);

            mappedChart.labels.push(parseWeekday(key));

            mappedChart.count += value;
        });

        store.commit("STATISTICS_SET_WEEKDAY", mappedChart);
    },

    setImpressionsData(dayInfo) {
        const mappedChart = {
            labels: [],
            datasets: [],
            count: [0, 0]
        };

        const impressionsChart = {
            label: i18n.t('message.statistics.message.impressions'),
            data: []
        };

        const orderedDates = orderDates(Object.keys(dayInfo));

        orderedDates.forEach(key => {
            const value = dayInfo[key].impress;

            impressionsChart.data.push(value);

            mappedChart.labels.push(
                formatDate({
                    date: key,
                    datasetSize: value
                })
            );

            mappedChart.count[0] += value;
        });

        mappedChart.datasets.push(impressionsChart);

        const viewabilityChart = {
            label: i18n.t('message.statistics.message.viewability'),
            data: []
        };

        orderedDates.forEach((key, index) => {
            const value = dayInfo[key].viewability;

            viewabilityChart.data.push(value);

            mappedChart.labels[index] = formatDate({
                date: key,
                datasetSize: mappedChart.labels[index].datasetSize + value
            });

            mappedChart.count[1] += value;
        });

        mappedChart.datasets.push(viewabilityChart);

        store.commit("STATISTICS_SET_IMPRESSIONS", mappedChart);
    },

    setTopLocalsData(topLocalsTypeData) {
        const topLocalsTypeChart = {
            label: i18n.t('message.statistics.message.topLocals'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [topLocalsTypeChart],
            count: topLocalsTypeData.totalAccountAccess
        };

        topLocalsTypeData.locals.forEach(({ localName, totalAccess }) => {
            topLocalsTypeChart.data.push(totalAccess);
            mappedChart.labels.push(localName);
        });

        store.commit("STATISTICS_SET_TOP_LOCALS", mappedChart);
    },

    setUserData(dayInfo) {
        const mappedChart = {
            labels: [],
            datasets: [],
            count: [0, 0]
        };

        const uniqueUserChart = {
            label: i18n.t('message.statistics.message.uniqueUsers'),
            data: []
        };

        const orderedDayInfoDates = orderDates(Object.keys(dayInfo));

        orderedDayInfoDates.forEach(key => {
            const value = dayInfo[key].uniqueUser;

            uniqueUserChart.data.push(value);

            if (!mappedChart.labels.includes(key)) {
                mappedChart.labels.push(key);
            }

            mappedChart.count[0] += value;
        });

        mappedChart.datasets.push(uniqueUserChart);

        const newUserChart = {
            label: i18n.t('message.statistics.message.newUsers'),
            data: []
        };

        orderedDayInfoDates.forEach(key => {
            const value = dayInfo[key].newUser;

            newUserChart.data.push(value);

            if (!mappedChart.labels.includes(key)) {
                mappedChart.labels.push(key);
            }

            mappedChart.count[1] += value;
        });

        const sortedLabels = orderDates(mappedChart.labels);

        mappedChart.labels = sortedLabels.map(dateKey => {
            return formatDate({
                date: dateKey
            });
        });

        mappedChart.datasets.push(newUserChart);

        store.commit("STATISTICS_SET_CLIENTS", mappedChart);
    },

    setAccessData(dayInfo) {
        const completeAccessChart = {
            label: i18n.t('message.statistics.message.completed'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [completeAccessChart],
            count: 0
        };

        const orderedDates = orderDates(Object.keys(dayInfo));

        orderedDates.forEach(key => {
            const value = dayInfo[key];

            const validAccess = value.access - value.abandonment;

            completeAccessChart.data.push(validAccess);

            mappedChart.labels.push(
                formatDate({
                    date: key,
                    datasetSize: validAccess
                })
            );

            mappedChart.count += validAccess;
        });

        store.commit("STATISTICS_SET_ACCESS", mappedChart);
    },

    setAdminAccessData(dayInfo) {
        const mappedChart = {
            labels: [],
            datasets: [],
            count: [0, 0]
        };

        const accessMappedChart = {
            label: i18n.t('message.statistics.message.completed'),
            labels: [],
            data: []
        };

        const orderedDates = orderDates(Object.keys(dayInfo));

        orderedDates.forEach(key => {
            const value = dayInfo[key];

            const validAccess = value.access - value.abandonment;

            accessMappedChart.data.push(validAccess);

            accessMappedChart.labels.push(
                formatDate({
                    date: key,
                    datasetSize: validAccess
                })
            );

            if (!mappedChart.labels.includes(key)) {
                mappedChart.labels.push(key);
            }

            mappedChart.count[0] += validAccess;
        });

        mappedChart.datasets.push(accessMappedChart);

        const abandonementMappedChart = {
            label: i18n.t('message.statistics.message.abandonment'),
            labels: [],
            data: []
        };

        orderedDates.forEach(key => {
            const value = dayInfo[key];

            const abandonment = value.abandonment;

            abandonementMappedChart.data.push(abandonment);

            abandonementMappedChart.labels.push(
                formatDate({
                    date: key,
                    datasetSize: abandonment
                })
            );

            if (!mappedChart.labels.includes(key)) {
                mappedChart.labels.push(key);
            }

            mappedChart.count[1] += abandonment;
        });

        mappedChart.datasets.push(abandonementMappedChart);

        const sortedLabels = orderDates(mappedChart.labels);

        mappedChart.labels = sortedLabels.map(dateKey => {
            return formatDate({
                date: dateKey
            });
        });

        store.commit("STATISTICS_SET_ACCESS", mappedChart);
    },

    setTotalUserData(dayInfo) {
        const totalUserChart = {
            label: i18n.t('message.statistics.message.completed'),
            data: []
        };

        const mappedChart = {
            labels: [],
            datasets: [totalUserChart],
            count: 0
        };

        const orderedDates = orderDates(Object.keys(dayInfo));

        orderedDates.forEach(key => {
            const value = dayInfo[key];

            const totalUser = value.totalUser;

            totalUserChart.data.push(totalUser);

            mappedChart.labels.push(
                formatDate({
                    date: key,
                    datasetSize: totalUser
                })
            );

            mappedChart.count += totalUser;
        });

        store.commit("STATISTICS_SET_TOTAL_USER", mappedChart);
    },

    // async clients({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.clients({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = lineMap({
    //         label: "Clientes",
    //         data,
    //     });

    //     store.commit("STATISTICS_SET_CLIENTS", mapped);

    //     return mapped;
    // },

    // async impressions({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.impressions({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = lineMap({
    //         label: "Impressões",
    //         data,
    //     });

    //     store.commit("STATISTICS_SET_IMPRESSIONS", mapped);

    //     return mapped;
    // },

    // async viewability({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.viewability({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = lineMap({
    //         label: "Viewability",
    //         data,
    //     });

    //     store.commit("STATISTICS_SET_VIEWABILITY", mapped);

    //     return mapped;
    // },

    // async gender({ date, devices, advertising, users, establishments, local }) {
    //     const data = await Vue.api.core.statistics.gender({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = doughnutOrBarMap({
    //         data,
    //     });

    //     store.commit("STATISTICS_SET_GENDER", mapped);

    //     return mapped;
    // },

    // async newByReturningClients({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.newByReturningClients({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = doughnutOrBarMap({
    //         data,
    //     });

    //     store.commit("STATISTICS_SET_NEW_BY_RETURNING_CLIENTS", mapped);

    //     return mapped;
    // },

    // async simultaneousConnections({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.simultaneousConnections({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = doughnutOrBarMap({
    //         data,
    //     });

    //     store.commit("STATISTICS_SET_NEW_BY_RETURNING_CLIENTS", mapped);

    //     return mapped;
    // },

    // async age({ date, devices, advertising, users, establishments, local }) {
    //     const data = await Vue.api.core.statistics.age({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = doughnutOrBarMap({
    //         data: data.dados,
    //         total: data.total,
    //         capitalCase: false,
    //         sortBy: "KEY",
    //     });

    //     store.commit("STATISTICS_SET_AGE", mapped);

    //     return mapped;
    // },

    // async screenTime({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.screenTime({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     store.commit("STATISTICS_SET_SCREEN_TIME_AVG", data);

    //     return data;
    // },

    // async conectionTime({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.conectionTime({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     store.commit("STATISTICS_SET_CONECTION_TIME_AVG", data);

    //     return data;
    // },

    // async rejectionEngagement({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.rejectionEngagement({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     store.commit("STATISTICS_SET_REJECTION", data.REJEICAO);
    //     store.commit("STATISTICS_SET_ENGAGEMENT", data.ENGAJAMENTO);

    //     return data;
    // },

    // async comments({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.comments({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     store.commit("STATISTICS_SET_COMMENTS", data);

    //     return data;
    // },

    // async registrationMethod({
    //     date,
    //     devices,
    //     advertising,
    //     users,
    //     establishments,
    //     local,
    // }) {
    //     const data = await Vue.api.core.statistics.registrationMethod({
    //         date,
    //         devices,
    //         advertising,
    //         users,
    //         establishments,
    //         local,
    //     });

    //     const mapped = doughnutOrBarMap({ data });

    //     store.commit("STATISTICS_SET_REGISTRATION_METHOD", mapped);

    //     return mapped;
    // },

    async overviewAdmin({
        date,
        devices,
        advertising,
        users,
        establishments,
        local
    } = {}) {
        const statisticsData = await Vue.api.core.statistics.overviewAdmin({
            date,
            devices,
            advertising,
            users,
            establishments,
            local
        });

        this.setAdminData(statisticsData);

        return statisticsData;
    },

    async authOverviewAdmin({
        date,
        devices,
        advertising,
        users,
        establishments,
        local
    } = {}) {
        const data = await Vue.api.authStatistics.overviewAdmin({
            date,
            devices,
            advertising,
            users,
            establishments,
            local
        });

        store.commit(
            "STATISTICS_SET_ACTIVE_ADVERTISERS",
            data.TOTAL_ADVERTISER
        );
        store.commit(
            "STATISTICS_SET_ACTIVE_ESTABLISHMENTS",
            data.TOTAL_ESTABLISHMENT
        );

        return data;
    },

    async excell({ filters }) {
        const excellArquive = await Vue.api.core.statistics.excell({ filters });

        return excellArquive;
    }
};

// const doughnutOrBarMap = ({
//     data,
//     total,
//     capitalCase = true,
//     sortBy = "VALUE",
// }) => {
//     const dataSet = {
//         data: [],
//     };

//     const mapped = {
//         total: total,
//         labels: [],
//         datasets: [dataSet],
//     };

//     data = _.map(data, (value, key) => {
//         return { key, value };
//     });

//     if (sortBy === "KEY")
//         data = _.sortBy(data, (item) => {
//             return item.key.replace(/\D/g, "");
//         });

//     if (sortBy === "VALUE")
//         data = _.orderBy(
//             data,
//             (item) => {
//                 return item.value;
//             },
//             "desc"
//         );

//     _.forEach(data, (item) => {
//         mapped.labels.push(capitalCase ? Case.capital(item.key) : item.key);
//         dataSet.data.push(item.value);
//     });

//     return mapped;
// };

// const lineMap = ({ label, data }) => {
//     const amount = {
//         label,
//         data: [],
//     };

//     const mapped = {
//         labels: [],
//         datasets: [amount],
//     };
//     let count = 0;

//     _.forEach(data, (statistic) => {
//         mapped.labels.push(
//             formatDate({
//                 date: statistic.data,
//                 datasetSize: data.length,
//             })
//         );
//         if (statistic.quantidade) {
//             count += statistic.quantidade;
//         }
//         amount.data.push(statistic.quantidade);
//     });
//     mapped.count = count;

//     return mapped;
// };

function parseDeviceType(deviceType) {
    return {
        [0]: "Android",
        [1]: "IOS",
        [2]: "Windows",
        [3]: "macOS",
        [4]: i18n.t('message.statistics.message.other')
    }[deviceType];
}

function parseRegisterType(registerType) {
    return {
        ["FACEBOOK"]: "Facebook",
        ["GOOGLE"]: "Google",
        ["FORMULARIO"]: i18n.t('message.statistics.message.form'),
        ["LINKEDIN"]: "Linkedin",
        ["MAC"]: i18n.t('message.statistics.message.noForm'),
        ["TWITTER"]: "Twitter",
        ["CPF"]: "CPF",
        ["ACTIVE_DIRECTORY"]: "Active Directory",
        ["GOV_BR"]: "Gov.br",
        ["ADFS_OPENID"]: "ADFS (OpenID)",
        ["ADFS_SAML"]: "ADFS (SAML)",
        ["BILLING"]: "Bilhetagem",
        ["CELLPHONE"]: i18n.t('message.loginPersonalize.message.telephone')
    }[registerType];
}

function parseGender(gender) {
    return {
        ["MASCULINO"]: i18n.t('message.statistics.message.male'),
        ["FEMININO"]: i18n.t('message.statistics.message.female'),
        ["NAO_DECLARADO"]: i18n.t('message.generics.message.notInformed')
    }[gender];
}

function parseWeekday(day) {
    return {
        ["MONDAY"]: i18n.t('message.statistics.message.monday'),
        ["TUESDAY"]: i18n.t('message.statistics.message.tuesday'),
        ["WEDNESDAY"]: i18n.t('message.statistics.message.wednesday'),
        ["THURSDAY"]: i18n.t('message.statistics.message.thursday'),
        ["FRIDAY"]: i18n.t('message.statistics.message.friday'),
        ["SATURDAY"]: i18n.t('message.statistics.message.saturday'),
        ["SUNDAY"]: i18n.t('message.statistics.message.sunday')
    }[day];
}

function orderDates(dates) {
    return dates.sort((dateA, dateB) => {
        return moment(dateA, "YYYY-MM-DD").diff(moment(dateB, "YYYY-MM-DD"));
    });
}

const formatDate = ({ date }) => {
    return moment(date, "YYYY-MM-DD").format("DD/MMM");
};
